import { Form, redirect, useActionData, useNavigation, useSearchParams, type ActionFunctionArgs } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { Me } from "@repo/shared-types";
import { api } from "@/lib/api";
import { requireSessionWith } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { formError, safeNext, type FormError } from "@/lib/session";
import { AuthCard, ErrorNote, Field, SubmitButton } from "@/components/form";
import { useUsernameWarning } from "@/components/username-warning";
import { DivisionField } from "@/routes/choose-password";
import { useSession } from "@/routes/root";

/**
 * About you (ADR 0076): where the api sends an account Google has just made,
 * once, to ask what the password page asks an email sign-up — a name, a
 * username, a division — then on to `next`, where the sign-in was going.
 * The account exists already, with Google's name and a generated handle, so
 * the page changes them; closed, it is not shown again, and Account changes
 * all three.
 */

export async function welcomeLoader({ request }: { request: Request }) {
  await requireSessionWith(request, () => read(queries.profile()));
  return null;
}

export async function welcomeAction({ request }: ActionFunctionArgs): Promise<FormError | Response> {
  const form = await request.formData();
  const username = String(form.get("username") ?? "").trim();
  const division = String(form.get("division") ?? "");
  const hadDivision = String(form.get("hadDivision") ?? "");

  try {
    // The name always, so one emptied clears Google's; the handle only when
    // one was typed, since the one generated is kept otherwise. A refused
    // handle saves neither, so the division waits for the next try too.
    // The division only when it changed, so Not now clears one it had.
    await api.me.update({ name: String(form.get("name") ?? "").trim(), ...(username ? { username } : {}) });
    if (division !== hadDivision) await api.me.profile.update({ division });
  } catch (error) {
    return formError(error, "Could not save");
  }
  return redirect(safeNext(form.get("next")));
}

export default function Welcome() {
  const session = useSession();
  // The loader let the page draw only with a session.
  return session && <AboutYou session={session} />;
}

function AboutYou({ session }: { session: Me }) {
  const { data: profile } = useSuspenseQuery(queries.profile());
  const [params] = useSearchParams();
  const result = useActionData() as FormError | undefined;
  const submitting = useNavigation().state === "submitting";
  const fieldError = (name: string) => (result?.field === name ? result.error : undefined);
  const warning = useUsernameWarning({
    says: (
      <>
        You'll be <strong>@{session.user.username}</strong>. You can change it any time in Account.
      </>
    ),
    accept: "Keep it",
  });

  return (
    <AuthCard title="About you" footer={null}>
      {/* Neutral, since anybody signed in can open the page by its address. */}
      <p className="text-sm text-muted-foreground">
        For your account, <strong>{session.user.email}</strong>.
      </p>
      {result?.error && !result.field && <ErrorNote message={result.error} />}

      <Form method="post" replace className="space-y-4" onSubmit={warning.onSubmit}>
        <input type="hidden" name="next" value={safeNext(params.get("next"))} />
        <input type="hidden" name="hadDivision" value={profile.division ?? ""} />
        <Field
          label="Name"
          name="name"
          autoComplete="name"
          required={false}
          optional
          maxLength={100}
          defaultValue={session.user.name ?? ""}
          error={fieldError("name")}
        />
        <Field
          label="Username"
          name="username"
          autoComplete="nickname"
          required={false}
          pattern="[A-Za-z0-9_\-]{3,30}"
          error={fieldError("username")}
          hint="How people find you, 3 to 30 of a–z, 0–9, - and _."
        />
        <DivisionField error={fieldError("division")} initial={profile.division ?? ""} />
        <SubmitButton pending={submitting}>{submitting ? "Saving…" : "Continue"}</SubmitButton>
      </Form>
      {warning.sheet}
    </AuthCard>
  );
}
